import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import { shake } from '../../utils/motion.config';
import UserProgressContext from '../../userProgressContext';

const FEEDBACK_DWELL_MS = 1500;

/**
 * BranchChoice — Narrative branch with 2–3 options and consequence visual.
 *
 * step.branches schema (from level JSON):
 *   { character, prompt, options: [{ id, text, consequence, response }], onComplete: "X.YY" }
 *
 * consequence === 'grid_status_green'  →  correct choice
 * consequence !== 'grid_status_green'  →  wrong choice
 *
 * Compatibility: also accepts option.isCorrect (bool) and option.feedback for alternate schemas.
 */
function BranchChoice({ step, onComplete, onWrongAnswer, onScoreUpdate }) {
  const { audioMuted, setNextButtonLocked } = useContext(UserProgressContext);

  const branches = step?.branches ?? {};
  const { character, prompt, options = [], onComplete: reconvergeKey } = branches;

  const [selectedOption, setSelectedOption] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [isCorrect, setIsCorrect] = useState(null);
  const [isShaking, setIsShaking] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [triedIds, setTriedIds] = useState(new Set());

  // Lock Next button — BranchChoice auto-advances; layout Next button must not skip the branch
  useEffect(() => {
    setNextButtonLocked(true);
    return () => setNextButtonLocked(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isOptionCorrect = (option) => {
    // Support both schema variants
    if ('isCorrect' in option) return option.isCorrect;
    return option.consequence === 'grid_status_green';
  };

  const getOptionFeedback = (option) => {
    return option.response ?? option.feedback ?? '';
  };

  const handleOptionClick = (option) => {
    if (selectedOption !== null || isAdvancing) return;

    const correct = isOptionCorrect(option);
    const newTried = new Set([...triedIds, option.id]);

    setSelectedOption(option);
    setFeedbackText(getOptionFeedback(option));
    setIsCorrect(correct);
    setTriedIds(newTried);

    if (!correct) {
      setIsShaking(true);
      onWrongAnswer?.(option.consequence);

      // Auto-advance only when the user has tried every single option
      // (i.e. no untried options remain at all, correct or otherwise)
      const allExhausted = options.every((o) => newTried.has(o.id));
      if (allExhausted) {
        setTimeout(() => {
          setIsAdvancing(true);
          onComplete?.(parseFloat(parseFloat(reconvergeKey).toFixed(2)));
        }, FEEDBACK_DWELL_MS);
      }
    } else {
      if (step.score) {
        onScoreUpdate?.(step.score);
      }
      setTimeout(() => {
        setIsAdvancing(true);
        onComplete?.(parseFloat(parseFloat(reconvergeKey).toFixed(2)));
      }, FEEDBACK_DWELL_MS);
    }
  };

  const handleTryAgain = () => {
    setSelectedOption(null);
    setFeedbackText('');
    setIsCorrect(null);
  };

  return (
    <div className="flex flex-col gap-4 p-6 text-white w-full h-full overflow-y-auto">
      {character && (
        <p className="text-sm text-indigo-300 uppercase tracking-wider font-semibold">
          {character}
        </p>
      )}
      {prompt && (
        <p className="text-lg md:text-xl font-semibold leading-relaxed">{prompt}</p>
      )}

      <div className="flex flex-col gap-3 mt-4">
        {options.map((option) => {
          const tried = triedIds.has(option.id);
          const optCorrect = isOptionCorrect(option);
          const isSelected = selectedOption?.id === option.id;
          return (
            <button
              key={option.id}
              onClick={() => handleOptionClick(option)}
              disabled={selectedOption !== null || isAdvancing}
              className={[
                'border rounded-lg px-4 py-3 text-left transition-all duration-200 text-sm md:text-base',
                selectedOption === null
                  ? 'border-indigo-400 hover:bg-indigo-700 cursor-pointer'
                  : isSelected && optCorrect
                  ? 'border-green-500 bg-green-800/40 text-green-200'
                  : isSelected && !optCorrect
                  ? 'border-red-500 bg-red-800/40 text-red-200'
                  : tried
                  ? 'border-gray-600 bg-gray-800/30 opacity-50'
                  : 'border-indigo-400 opacity-60',
              ].join(' ')}
            >
              {option.text}
            </button>
          );
        })}
      </div>

      {feedbackText && (
        <motion.div
          animate={isShaking ? shake : {}}
          onAnimationComplete={() => setIsShaking(false)}
          className={[
            'mt-4 p-4 rounded-lg text-sm md:text-base font-medium',
            isCorrect
              ? 'bg-green-700/50 text-green-200 border border-green-600'
              : 'bg-red-700/50 text-red-200 border border-red-600',
          ].join(' ')}
        >
          {feedbackText}
        </motion.div>
      )}

      {isCorrect === false && !isAdvancing && (
        <button
          onClick={handleTryAgain}
          className="mt-2 self-start bg-indigo-500 hover:bg-indigo-600 text-white py-1 px-4 rounded-lg text-sm transition-colors"
        >
          Try Again
        </button>
      )}
    </div>
  );
}

export default BranchChoice;
